import { PropsWithChildren, ReactNode, forwardRef } from 'react';

import classNames from 'classnames';

import { ScrollTopButton } from '../ScrollTopButton';
import { BackstagePage } from './BackstagePage';
import { Content } from './Content';
import { Header, HeaderProps } from './Header';
import { HeaderContent } from './HeaderContent';
import { HeaderTabsContainer } from './HeaderTabsContainer';
import { NarrowBoxProps } from './NarrowBox';
import { PageTitle } from './PageTitle';
import { SidebarContent, SidebarContentProps } from './SidebarContent';
import { hasPageLayoutHeader } from './hasPageLayoutHeader';

import styles from './PageLayout.module.css';

export type PageLayoutProps = PropsWithChildren &
  Pick<
    HeaderProps,
    | 'title'
    | 'headerBreadcrumbs'
    | 'headerTabs'
    | 'stickyHeader'
    | 'stickyHeaderTabs'
    | 'headerTabsRef'
    | 'isLoading'
    | 'pageTitleOverride'
    | 'enableMargin'
  > &
  Pick<SidebarContentProps, 'sideBarRight' | 'sideBarLeft'> & {
    narrow?: boolean | NarrowBoxProps['width'];
    pageHeader?: ReactNode;
    headerContent?: ReactNode;
    scrollButton?: boolean;
    noPadding?: boolean;
    floatingHeader?: ReactNode;
    subheader?: ReactNode;
    fullViewport?: boolean;
    contentOverflow?: 'auto' | 'hidden';
  };

export const PageLayout = forwardRef<HTMLDivElement, PageLayoutProps>(
  (
    {
      children,
      floatingHeader,
      sideBarRight,
      sideBarLeft,
      pageHeader,
      headerContent,
      stickyHeaderTabs = true,
      scrollButton = false,
      narrow,
      noPadding,
      fullViewport,
      contentOverflow = 'hidden',
      ...headerProps
    },
    ref,
  ) => {
    const hasHeader = hasPageLayoutHeader({
      ...headerProps,
      headerContent,
    });

    return (
      <BackstagePage ref={ref} fullViewport={fullViewport}>
        {floatingHeader}
        {scrollButton && <ScrollTopButton  data-track-source-anchor="f39cdcbde1;page-layout-scroll-top-button"/>}
        {pageHeader ??
          (hasHeader ? (
            <Header
              {...headerProps}
              narrow={narrow}
              stickyHeaderTabs={stickyHeaderTabs}
            >
              {headerContent ? (
                <HeaderContent>{headerContent}</HeaderContent>
              ) : null}
            </Header>
          ) : (
            headerProps.pageTitleOverride && (
              <PageTitle title={headerProps.pageTitleOverride} />
            )
          ))}
        {pageHeader && headerProps.headerTabs && (
          <HeaderTabsContainer
            sticky={stickyHeaderTabs}
            narrow={narrow}
            ref={headerProps.headerTabsRef}
          >
            {headerProps.headerTabs}
          </HeaderTabsContainer>
        )}

        <Content
          narrow={narrow}
          className={classNames(noPadding ? undefined : styles.content, {
            [styles.fullViewportContent]: fullViewport,
            [styles.scrollableFullViewportContent]:
              fullViewport && contentOverflow === 'auto',
          })}
          noPadding={noPadding}
        >
          {sideBarLeft || sideBarRight ? (
            <SidebarContent
              sideBarLeft={sideBarLeft}
              sideBarRight={sideBarRight}
            >
              {children}
            </SidebarContent>
          ) : (
            children
          )}
        </Content>
      </BackstagePage>
    );
  },
);
PageLayout.displayName = 'PageLayout';
